import { type ReactNode, useEffect } from "react";

import Box from "@mui/material/Box";

import { useDismissibleBanner } from "hooks/use-dismissible-banner.hook";

import { MENTIONS_LAUNCH_KEYS } from "./mentions-launch";
import useMentionsLaunchUserId from "./use-mentions-launch-user-id.hook";

const TABS = {
  bellTab: "mentions_bell_tab_new",
  homeTab: "mentions_home_tab_new"
} as const;

/**
 * A tab label with a small "NEW" chip after it, for a tab that team @mentions added: the bell's
 * Mentions tab (`bellTab`) and Home → Tasks → Mentioned Me (`homeTab`). The chip shows until the
 * person opens the tab, or 30 days after it first appeared. Flag off or no user yet: the label
 * exactly as it was.
 */
export default function MaybeMentionsNewTabLabel({
  tab,
  isActive,
  children
}: {
  tab: keyof typeof TABS;
  /** The tab is the selected one: opening it is what the chip asked for. */
  isActive: boolean;
  children: ReactNode;
}) {
  const userId = useMentionsLaunchUserId();
  if (!userId) return <>{children}</>;
  return (
    <NewTabLabel
      storageKey={MENTIONS_LAUNCH_KEYS[tab]}
      userId={userId}
      isActive={isActive}
      actionPrefix={TABS[tab]}
    >
      {children}
    </NewTabLabel>
  );
}

// Mounted only where the label is on screen, so "shown" is only reported when someone could
// actually see it.
function NewTabLabel({
  storageKey,
  userId,
  isActive,
  actionPrefix,
  children
}: {
  storageKey: string;
  userId: string;
  isActive: boolean;
  /** Datadog actions are `${actionPrefix}_shown` / `${actionPrefix}_dismissed`. */
  actionPrefix: string;
  children: ReactNode;
}) {
  const { visible, dismiss } = useDismissibleBanner({
    syncAcrossInstances: true,
    storageKey,
    userId,
    datadogActions: {
      shown: `${actionPrefix}_shown`,
      dismissed: `${actionPrefix}_dismissed`
    }
  });

  useEffect(() => {
    if (isActive && visible) dismiss({ source: "opened_tab" });
  }, [isActive, visible, dismiss]);

  return (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center" }}>
      {children}
      {visible && (
        <Box
          component="span"
          data-testid="mentions-new-chip"
          sx={{
            // Clears a count badge on the label before it (the bell tab's, up to "99+").
            ml: 2,
            px: 0.75,
            py: "1px",
            borderRadius: "999px",
            bgcolor: "warning.light",
            color: "warning.contrastText",
            fontSize: 12,
            fontWeight: 700,
            lineHeight: 1.4,
            letterSpacing: "0.04em"
          }}
        >
          NEW
        </Box>
      )}
    </Box>
  );
}
